/**
 * The middle cells of a board row. Schedule is computed from config (with
 * the days it runs when not every day — Phase 33), Danish first
 * (15/10/2026): the schedule reads on the Danish clock, the country's times
 * on hover; a 🌙 line says when drop times fall outside the window
 * (18/10/2026). The four day-plan cells — Today's list · List group ·
 * Progress · State — are day-plan/PlanCells; until the day plan answers they
 * are placeholders (23/10/2026, client QA: "the containers looks empty at the
 * first load, but there's no load").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ArrowRight, Clock, Moon } from 'lucide-react';
import { describeRunDays } from '@trafficpulse/shared';

import type { DayPlanContainerRow, DropGenContainerSummary } from '../../drop-generator.types';
import { type ClockCtx, dkLabel } from '../../utils/clock/dk-label';
import { minutesLabel } from '../../utils/format';
import { outsideOf } from '../../utils/drops/outside-window';
import type { ContainerTimes } from '../../utils/slot-times';
import { type CapCuts, NO_CUTS } from '../shared/cap/cap-cuts';
import { useCountryClock } from '../shared/time/country-clock-context';
import { OutsideWindowMark } from './outside/OutsideWindowMark';
import { useBoardPreviewState } from './day-plan/board-preview-state';
import { PendingCells } from './day-plan/PendingCells';
import { PlanCells } from './day-plan/PlanCells';
import type { LiveAutoSend } from '../shared/StateBadges';

export const scheduleLine = (
  container: DropGenContainerSummary,
  times: ContainerTimes | undefined,
  clock?: ClockCtx,
): { icon: 'clock' | 'arrow' | 'moon'; text: string; country?: string } => {
  if (container.startMode === 'AFTER_PREVIOUS') {
    const gap = container.gapMin > 0 ? ` + ${container.gapMin}m` : '';
    return { icon: 'arrow', text: `after prev${gap}` };
  }
  // The stored start is Danish (15/10/2026) — only the timing mirror's country minute is a start here.
  const start = times?.startMin ?? 0;
  const end = times?.lastPlannedMin ?? start;
  const icon = start > 1439 || start < 300 ? 'moon' : 'clock';
  const local = end === start ? minutesLabel(start) : `${minutesLabel(start)} – ${minutesLabel(end)}`;
  const a = clock ? dkLabel(start, clock.tz, clock.runDate) : null;
  const b = clock ? dkLabel(end, clock.tz, clock.runDate) : null;
  if (!clock || !a || !b) return { icon, text: local };
  return {
    icon,
    text: `${end === start ? a : `${a} – ${b}`} 🇩🇰`,
    country: `${clock.flag} ${local}`,
  };
};

const ICONS = { clock: Clock, arrow: ArrowRight, moon: Moon } as const;

interface Props {
  container: DropGenContainerSummary;
  times: ContainerTimes | undefined;
  recipientTimezone: string | null;
  preview: DayPlanContainerRow | undefined;
  /** 20/10/2026 — the slots the daily SMS cap cut today. */
  cuts?: CapCuts;
  /** Phase 9/20 — this container's auto-send as the day shows it. */
  autoSend?: LiveAutoSend;
  /** Phase 33 — the day plan's run date (recipient-local); null = unknown. */
  runDate?: string | null;
}

export const ContainerRowCells: FC<Props> = ({ container, times, recipientTimezone, preview, autoSend = null, runDate = null, cuts = NO_CUTS }) => {
  const page = useCountryClock();
  const clock = {
    tz: page.tz ?? recipientTimezone,
    flag: page.flag,
    runDate: runDate ?? page.runDate,
  };
  const schedule = scheduleLine(container, times, clock);
  const Icon = ICONS[schedule.icon];
  const days = describeRunDays(container.runDays);
  const planState = useBoardPreviewState();
  return (
    <>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span
          className="flex items-center gap-1.5 font-mono text-xs tabular-nums"
          title={schedule.country ? `The country's time: ${schedule.country}` : undefined}
        >
          <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="truncate">{schedule.text}</span>
        </span>
        {days !== 'every day' ? <span className="truncate text-[10px] text-muted-foreground">{days}</span> : null}
        {container.enabled ? <OutsideWindowMark outside={outsideOf(times)} /> : null}
      </span>
      {planState !== 'ready' && container.enabled ? (
        <PendingCells state={planState} />
      ) : (
        <PlanCells container={container} preview={preview} clock={clock} cuts={cuts} autoSend={autoSend} runDate={runDate} />
      )}
    </>
  );
};
